// The game's building blocks, ported from design/hot-type/boards/kit.js: tiles, board, keyboard,
// ash pile, live preview, rank ladder, score head, share grid and the score histogram.
import { memo, useMemo, type CSSProperties, type MouseEvent, type ReactNode } from 'react'
import type { EditionInfo, RankStep } from '@wbwl/contract'
import { letterPoints, rankOf, RULES_VERSION, SCORING, scoreWord } from '@wbwl/rules'
import { burnCols, isBurn, lettersToBurn } from '../lib/game.ts'
import { Icon } from './Icon.tsx'
import { canCatch, Scorch, SCORCH_MIN, scorchPlan } from './scorch.tsx'

type Gold = EditionInfo['gold']
type BoardLike = Pick<EditionInfo, 'gold' | 'burns' | 'rulesVersion'>
const cssVars = (vars: Record<string, string | number | undefined>): CSSProperties => vars as CSSProperties

// ---------- Tile ----------

export interface TileProps {
  l?: string
  /** Space-separated state classes: typed cur played burn burned gold ash spent used phx dim flare sm pop */
  k?: string
  size?: number
  gold?: Gold
  pt?: boolean
  gb?: boolean
  fl?: boolean
  /** Rules version for the points in the corner (capped at 5 from v2). */
  rv?: number
  /** Shapes a burned tile's char, so tiles on a board differ; the cell's place is a good one. */
  seed?: number
  /** A burned tile catching fire right now: its column, which times its flip and catch. */
  catching?: number
  style?: CSSProperties
}

export function Tile({ l = '', k = '', size = 52, gold, pt = true, gb = true, fl = true, rv = RULES_VERSION, seed, catching, style }: TileProps) {
  const tier = l && gold ? gold[l] : undefined
  const isAsh = /\bash\b/.test(k)
  // Burned tiles big enough to show it are Scorched: charred rim, glowing burn line (scorch.tsx).
  const scorched = /\bplayed\b/.test(k) && /\bburned\b/.test(k) && size >= SCORCH_MIN
  const s = seed ?? (l ? l.charCodeAt(0) : 1)
  const plan = useMemo(() => (scorched ? scorchPlan(s, size, catching) : null), [scorched, s, size, catching])
  return (
    <div className={`t ${k}${plan ? ' scorched' : ''}${plan?.fire ? ' ig' : ''}`} style={{ ...cssVars({ '--t': `${size}px` }), ...plan?.fire?.vars, ...style }}>
      {plan && <Scorch plan={plan} />}
      {l}
      {l && pt && !isAsh && <span className="pt">{letterPoints(l, rv)}</span>}
      {tier && gb && /\bgold\b/.test(k) && <span className="gb">+{tier}</span>}
      {l && fl && /\bburn(ed)?\b/.test(k) && <span className="fl"><Icon name="flame" /></span>}
      {!l && /\bburn\b/.test(k) && <span className="ghost-fl"><Icon name="flame" /></span>}
    </div>
  )
}

// ---------- Board ----------

export interface BoardProps {
  edition: BoardLike
  rows: readonly string[]
  /** The word being typed on the active row; undefined means no active row (phoenix, done). */
  typing?: string
  size?: number
  gap?: number
  scores?: boolean
  dimPlayed?: boolean
  shakeRow?: number | null
  /** Rows to show; 6 normally. */
  rowCount?: number
  ashes?: readonly string[]
  /** Optional invalid flag for the typed word (shows ✕ instead of a live score). */
  invalid?: boolean
  /** The row just played, which flips in; its burned tiles catch fire. */
  flipRow?: number | null
}

export function Board({ edition, rows, typing, size = 52, gap = 6, scores = true, dimPlayed, shakeRow, rowCount = 6, invalid, flipRow }: BoardProps) {
  const active = typing === undefined ? -1 : rows.length
  // From rules v2 a repeated gold letter adds nothing, so only its first tile wears the +bonus.
  const once = SCORING[edition.rulesVersion]?.goldOncePerWord
  const out: ReactNode[] = []
  for (let r = 0; r < rowCount; r++) {
    const tiles: ReactNode[] = []
    let rs: ReactNode = <div className="rs" />
    if (r < rows.length) {
      const w = rows[r]
      for (let c = 0; c < 5; c++) {
        const l = w[c]
        const burned = isBurn(edition, r, c)
        // A burned tile in the row just played flips in as part of its own catch (app.css .t.ig).
        const catching = flipRow === r && burned && canCatch(size)
        const k = ['played', edition.gold[l] ? 'gold' : '', burned ? 'burned' : '', dimPlayed ? 'dim' : '', flipRow === r && !catching ? 'flip' : ''].join(' ')
        tiles.push(<Tile key={c} l={l} k={k} size={size} gold={edition.gold} gb={false} rv={edition.rulesVersion} seed={r * 5 + c + 1} catching={catching ? c : undefined} />)
      }
      const s = scoreWord(w, edition)
      rs = <div className="rs"><b>{s.total}</b><small>{s.pts}×{s.mult}</small></div>
    } else if (r === active) {
      const t = typing ?? ''
      for (let c = 0; c < 5; c++) {
        const l = t[c]
        const k = [l ? 'typed pop' : 'cur', l && edition.gold[l] ? 'gold' : '', isBurn(edition, r, c) ? 'burn' : ''].join(' ')
        tiles.push(<Tile key={`${c}${l ?? ''}`} l={l} k={k} size={size} gold={edition.gold} gb={!(once && t.indexOf(l) < c)} rv={edition.rulesVersion} />)
      }
      if (invalid) rs = <div className="rs live"><b style={{ color: 'var(--burn)' }}>✕</b></div>
      else if (t.length === 5) {
        const s = scoreWord(t, edition)
        rs = <div className="rs live on"><b>{s.total}</b><small>{s.pts}×{s.mult}</small></div>
      } else rs = <div className="rs live"><b>—</b></div>
    } else {
      for (let c = 0; c < 5; c++) tiles.push(<Tile key={c} k={isBurn(edition, r, c) ? 'burn' : ''} size={size} />)
    }
    out.push(
      <div key={r} className={`brow${shakeRow === r ? ' shake' : ''}`}>
        {scores && <div className={`ls${r === active ? ' on' : ''}`}>{r === active ? '▸' : r + 1}</div>}
        <div className="tiles">{tiles}</div>
        {scores && rs}
      </div>,
    )
  }
  return <div className="board" style={cssVars({ '--gap': `${gap}px` })}>{out}</div>
}

// ---------- Keyboard ----------

// Memoised: typing a letter changes the board, not the keys (the game passes a steady onKey).
export const Keyboard = memo(function Keyboard({ edition, ashes, onKey, enterLabel = 'Enter', disabled }: { edition: BoardLike; ashes: readonly string[]; onKey(key: string): void; enterLabel?: string; disabled?: boolean }) {
  // Keys never take focus, so a physical Enter can't also "click" the last key tapped.
  const press = (key: string) => (e: MouseEvent) => { e.preventDefault(); if (!disabled) onKey(key) }
  const key = (l: string) => {
    const tier = edition.gold[l]
    const burned = ashes.includes(l)
    // Each key carries its letter's points, as the tiles do.
    const pts = letterPoints(l, edition.rulesVersion)
    return (
      <button key={l} type="button" tabIndex={-1} className={['k', tier && !burned ? 'gold' : '', burned ? 'burned' : ''].join(' ')} onMouseDown={(e) => e.preventDefault()} onClick={press(l)} aria-label={burned ? `${l}, burned` : `${l}, ${pts} point${pts === 1 ? '' : 's'}${tier ? `, gold plus ${tier}` : ''}`}>
        {l}
        {tier ? <span className="gb">+{tier}</span> : null}
        <span className="pt" aria-hidden="true">{pts}</span>
      </button>
    )
  }
  return (
    <div className="kb" role="group" aria-label="Keyboard">
      <div className="kb-row">{[...'QWERTYUIOP'].map(key)}</div>
      <div className="kb-row">{[...'ASDFGHJKL'].map(key)}</div>
      <div className="kb-row">
        <button type="button" tabIndex={-1} className="k wide enter" onMouseDown={(e) => e.preventDefault()} onClick={press('ENTER')}>{enterLabel}</button>
        {[...'ZXCVBNM'].map(key)}
        <button type="button" tabIndex={-1} className="k wide" aria-label="Delete" onMouseDown={(e) => e.preventDefault()} onClick={press('DEL')}><Icon name="del" size={22} /></button>
      </div>
    </div>
  )
})

// ---------- Ashes ----------

/**
 * The ash pile, one slot per burn tile. With `align` it sits on the board's own grid: the flame in
 * the row-number gutter, the slots spanning exactly the five tile columns, and the label in the
 * score column, so it never runs past the board's edges.
 */
export function Ashes({ edition, ashes, size = 26, label = 'Ashes', used, hideLast = 0, align }: { edition: BoardLike; ashes: readonly string[]; size?: number; label?: string | false; used?: string; hideLast?: number; align?: { tile: number; gap: number } }) {
  const shown = hideLast ? ashes.slice(0, -hideLast) : ashes
  const slot = (l: string | undefined, i: number, sz: number) => {
    const k = ['ash', 'sm', l ? '' : 'empty', l && edition.gold[l] ? 'gold' : '', used && l && used.includes(l) ? 'used' : ''].join(' ')
    return <Tile key={i} l={l} k={k} size={sz} pt={false} gold={edition.gold} />
  }
  if (align) {
    const width = 5 * align.tile + 4 * align.gap
    const n = edition.burns.length
    const slotGap = 4
    const sz = Math.min(34, Math.floor((width - (n - 1) * slotGap) / n))
    return (
      <div className="ashes aligned" style={cssVars({ '--gap': `${align.gap}px` })}>
        <div className="ls" aria-hidden="true"><Icon name="flame" /></div>
        <div className="slots" style={{ width, gap: slotGap }} aria-label={`Ashes: ${shown.join(' ') || 'none yet'}`}>{edition.burns.map((_, i) => slot(shown[i], i, sz))}</div>
        <div className="rs">{label || ''}</div>
      </div>
    )
  }
  return (
    <div className="ashes">
      {label !== false && <div className="lbl"><Icon name="flame" />{label}</div>}
      <div className="slots">{edition.burns.map((_, i) => slot(shown[i], i, size))}</div>
    </div>
  )
}

// ---------- Live preview ----------

export function Preview({ edition, word, row, ashes, invalid }: { edition: BoardLike; word: string; row: number | null; ashes: readonly string[]; invalid?: boolean }) {
  if (!word) return <div className="preview"><span className="math">Type a word · letters × multiplier</span></div>
  if (invalid) return <div className="preview"><span className="math">{word} isn't in the word list</span><span className="eq faint" style={{ marginLeft: 'auto' }}>✕</span></div>
  const s = scoreWord(word, edition)
  const burns = row === null ? [] : lettersToBurn(edition, row, word, ashes)
  return (
    <div className="preview">
      <span className="math"><b>{s.pts}</b> pts × <em>{s.mult}</em></span>
      {word.length < 5 ? <span className="eq faint" style={{ marginLeft: 'auto', fontSize: 15 }}>so far</span> : <span className="eq" style={{ marginLeft: 'auto' }}>{s.total}</span>}
      {burns.length > 0 && <span className="chip burn" style={{ height: 26 }}><Icon name="flame" />{burns.join(', ')} burn{burns.length === 1 ? 's' : ''}</span>}
    </div>
  )
}

// ---------- Ladder & score head ----------

export function Ladder({ total, ranks, width = 168 }: { total: number; ranks: readonly RankStep[]; width?: number | string }) {
  const st = rankOf(total, ranks)
  return (
    <div className="ladder" style={{ width }}>
      {ranks.map((_, i) => {
        const top = i === ranks.length - 1
        if (i < st.index) return <i key={i} className={`on${top ? ' top' : ''}`} />
        if (i === st.index) return top ? <i key={i} className="on top" /> : <i key={i} className="part" style={cssVars({ '--p': `${Math.round(st.progress * 100)}%` })} />
        return <i key={i} />
      })}
    </div>
  )
}

/** Your score and rank; `lead` goes first in the row (the back button, on desktop) and `label` names the score. */
export function ScoreHead({ total, ranks, ladderWidth = 168, lead, label = 'Score' }: { total: number; ranks: readonly RankStep[]; ladderWidth?: number; lead?: ReactNode; label?: string }) {
  const st = rankOf(total, ranks)
  return (
    <div className="scorehead">
      {lead}
      <div><div className="lbl">{label}</div><div className="num">{total.toLocaleString('en-US')}</div></div>
      <div className="rank">
        <span className="rankname">{st.rank.name}</span>
        <Ladder total={total} ranks={ranks} width={ladderWidth} />
        <span className="ladder-lbl">{st.next ? <><b>{st.toNext}</b> to {st.next.name}</> : <b>Top rank</b>}</span>
      </div>
    </div>
  )
}

// ---------- Share grid ----------

export function MiniGrid({ edition, rows, m = 10 }: { edition: BoardLike; rows: readonly string[]; m?: number }) {
  const cells: ReactNode[] = []
  for (let r = 0; r < 6; r++) {
    for (let c = 0; c < 5; c++) {
      const w = rows[r]
      const cls = !w ? 'e' : isBurn(edition, r, c) ? 'b' : edition.gold[w[c]] ? 'g' : ''
      cells.push(<i key={`${r}-${c}`} className={cls} />)
    }
  }
  return <div className="mgrid" style={cssVars({ '--m': `${m}px` })} aria-hidden="true">{cells}</div>
}

// ---------- Word tiles (scorecards) ----------

export function WordTiles({ edition, word, row, size = 26, gap = 3, k = 'played' }: { edition: BoardLike; word: string; row?: number; size?: number; gap?: number; k?: string }) {
  const burns = row === undefined ? [] : burnCols(edition, row)
  const once = SCORING[edition.rulesVersion]?.goldOncePerWord
  return (
    <div style={{ display: 'flex', gap }}>
      {[...word].map((l, i) => {
        const burn = burns.includes(i)
        return <Tile key={i} l={l} k={[k, edition.gold[l] ? 'gold' : '', burn ? (k === 'played' ? 'burned' : 'burn') : ''].join(' ')} size={size} pt={false} gb={size >= 34 && !(once && word.indexOf(l) < i)} fl={size >= 30} gold={edition.gold} seed={(row ?? 0) * 5 + i + 1} />
      })}
    </div>
  )
}

// ---------- Histogram ----------

export function Histogram({ bins, you, height = 60, label = true }: { bins: readonly number[]; you: number | null; height?: number; label?: boolean }) {
  const max = Math.max(1, ...bins)
  const yb = you === null ? -1 : Math.floor(you / 50)
  const count = Math.max(bins.length, yb + 1, 12)
  const list = Array.from({ length: count }, (_, i) => bins[i] ?? 0)
  return (
    <div style={{ position: 'relative', paddingTop: label && you !== null ? 16 : 0 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 2, height, position: 'relative' }}>
        {list.map((v, b) => (
          <i key={b} style={{ flex: 1, height: Math.max(3, Math.round((v / max) * height)), borderRadius: '2px 2px 0 0', background: b === yb ? 'var(--ink)' : b > yb && yb >= 0 ? 'color-mix(in srgb, var(--ink-3) 35%, transparent)' : 'var(--rule-2)' }} />
        ))}
        {label && you !== null && <span style={{ position: 'absolute', left: `${((yb + 0.5) / count) * 100}%`, top: -4, transform: 'translate(-50%, -100%)', font: '700 10.5px/1 var(--mono)', whiteSpace: 'nowrap' }}>You · {you}</span>}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 6, font: '500 10px/1 var(--mono)', color: 'var(--ink-3)' }}><span>0</span><span>{(count * 50).toLocaleString('en-US')}</span></div>
    </div>
  )
}
